import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { Person, PaginatedResponse } from '@nasabtree/shared-types';

interface PersonPickerProps {
  treeId: string;
  value: string | null;
  onChange: (personId: string, person: Person) => void;
  excludeIds?: string[];
  genderFilter?: 'male' | 'female';
  placeholder?: string;
  defaultFamilyName?: string;
}

export function PersonPicker({
  treeId,
  value,
  onChange,
  excludeIds = [],
  genderFilter,
  placeholder,
  defaultFamilyName,
}: PersonPickerProps) {
  const { t } = useTranslation();
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createFamilyName, setCreateFamilyName] = useState(defaultFamilyName ?? '');
  const [createGender, setCreateGender] = useState<'male' | 'female'>(genderFilter ?? 'male');
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);

  // Load selected person details on mount if value is set
  useEffect(() => {
    if (value && !selectedPerson) {
      apiFetch<Person>(`/api/trees/${treeId}/persons/${value}`).then((r) => {
        if (r.ok) setSelectedPerson(r.data);
      });
    }
  }, [value, treeId, selectedPerson]);

  const search = useCallback(
    async (q: string) => {
      setLoading(true);
      const params = new URLSearchParams({ page_size: '10' });
      if (q) params.set('q', q);
      const res = await apiFetch<PaginatedResponse<Person>>(
        `/api/trees/${treeId}/persons?${params.toString()}`,
      );
      if (res.ok) {
        let items = res.data.items;
        if (excludeIds.length > 0) {
          const exclude = new Set(excludeIds);
          items = items.filter((p) => !exclude.has(p.id));
        }
        if (genderFilter) {
          items = items.filter((p) => p.gender === genderFilter);
        }
        setResults(items);
      }
      setLoading(false);
    },
    [treeId, excludeIds, genderFilter],
  );

  // Debounced search when query changes
  useEffect(() => {
    if (!searching) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void search(query), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, searching, search]);

  // Auto-focus search input when opening
  useEffect(() => {
    if (searching) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [searching]);

  function openSearch() {
    setSearching(true);
    setQuery('');
    setShowCreate(false);
  }

  function handleSelect(person: Person) {
    setSelectedPerson(person);
    onChange(person.id, person);
    setSearching(false);
    setQuery('');
    setShowCreate(false);
  }

  async function handleCreate() {
    if (!createName.trim()) return;
    setCreating(true);
    const res = await apiFetch<Person>(`/api/trees/${treeId}/persons`, {
      method: 'POST',
      body: JSON.stringify({
        ism_ar: createName.trim(),
        family_name: createFamilyName.trim() || null,
        gender: createGender,
      }),
    });
    if (res.ok) {
      handleSelect(res.data);
    }
    setCreating(false);
  }

  // Selected state — show the chosen person with a change button
  if (selectedPerson && !searching) {
    return (
      <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white">
        <span
          className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${
            selectedPerson.gender === 'male'
              ? 'bg-sky-50 text-sky-700'
              : 'bg-pink-50 text-pink-700'
          }`}
        >
          {selectedPerson.gender === 'male' ? t('persons.gender.male') : t('persons.gender.female')}
        </span>
        <span dir="rtl" className="text-sm truncate flex-1">
          {selectedPerson.ism_ar ?? selectedPerson.ism_en ?? '—'}
        </span>
        <button
          type="button"
          onClick={openSearch}
          className="text-xs text-brand-600 hover:text-brand-700 font-medium shrink-0"
        >
          {t('common.change') ?? 'Change'}
        </button>
      </div>
    );
  }

  // Search state — inline search input + results list
  if (searching) {
    return (
      <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
        {/* Search input */}
        <div className="p-2 border-b border-gray-100">
          <input
            ref={inputRef}
            dir="auto"
            placeholder={t('personPicker.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          />
        </div>

        {/* Results */}
        <div className="overflow-y-auto max-h-48">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-4 h-4 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-gray-400">
              {t('personPicker.noResults')}
            </div>
          ) : (
            results.map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => handleSelect(person)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-brand-50 transition-colors text-start"
              >
                <span
                  className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${
                    person.gender === 'male'
                      ? 'bg-sky-50 text-sky-700'
                      : 'bg-pink-50 text-pink-700'
                  }`}
                >
                  {person.gender === 'male' ? t('persons.gender.male') : t('persons.gender.female')}
                </span>
                <span dir="rtl" className="truncate">
                  {person.ism_ar ?? person.ism_en ?? '—'}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Create new person */}
        <div className="border-t border-gray-100 p-2">
          {showCreate ? (
            <div className="space-y-2">
              <input
                dir="rtl"
                placeholder={t('persons.ism_ar')}
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <input
                dir="rtl"
                placeholder={t('persons.family_name')}
                value={createFamilyName}
                onChange={(e) => setCreateFamilyName(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              {!genderFilter && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateGender('male')}
                    className={`flex-1 text-xs py-1 rounded-lg border font-medium ${
                      createGender === 'male'
                        ? 'border-sky-300 bg-sky-50 text-sky-700'
                        : 'border-gray-200 text-gray-500'
                    }`}
                  >
                    {t('persons.gender.male')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCreateGender('female')}
                    className={`flex-1 text-xs py-1 rounded-lg border font-medium ${
                      createGender === 'female'
                        ? 'border-pink-300 bg-pink-50 text-pink-700'
                        : 'border-gray-200 text-gray-500'
                    }`}
                  >
                    {t('persons.gender.female')}
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 text-xs py-1.5 text-gray-500 hover:bg-gray-50 rounded-lg"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => void handleCreate()}
                  disabled={creating || !createName.trim()}
                  className="flex-1 text-xs py-1.5 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 font-medium"
                >
                  {creating ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-600 hover:bg-brand-50 rounded-lg transition-colors font-medium"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
              </svg>
              {t('personPicker.createNew')}
            </button>
          )}
        </div>

        {/* Cancel search */}
        <div className="border-t border-gray-100 px-2 py-1.5">
          <button
            type="button"
            onClick={() => setSearching(false)}
            className="w-full text-xs text-gray-400 hover:text-gray-600 py-1"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // Empty state — show button to start searching
  return (
    <button
      type="button"
      onClick={openSearch}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-start focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white text-gray-400"
    >
      {placeholder ?? t('personPicker.search')}
    </button>
  );
}
